/**
 * PostHog client initialisation for the IbadaCloud tenant app.
 *
 * Imported as the VERY FIRST import in `src/main.tsx` so the SDK is
 * ready before the React tree mounts and any early errors / route
 * transitions can be captured.
 *
 * Production-only triple gate (same shape we use for any third-party
 * telemetry in this app):
 *
 *   1. `import.meta.env.PROD` — true only in a Vite production build.
 *   2. `VITE_APP_ENV === "production"` — set explicitly by the real
 *      production deploy pipeline. Preview / staging / branch deploys
 *      must set a different value (or leave it unset) so they don't
 *      pollute the production project.
 *   3. `VITE_POSTHOG_PROJECT_TOKEN` is present — if the token is
 *      missing for any reason the SDK stays dormant rather than
 *      failing loudly.
 *
 * If any gate fails, `posthog.init` is skipped. `<PostHogProvider>` in
 * `main.tsx` is still mounted unconditionally; when the SDK isn't
 * initialised, `usePostHog()` returns the uninitialised client and
 * `posthog?.capture(...)` calls no-op silently — the intended
 * behaviour for dev / preview / staging.
 *
 * Phase 1 surface (intentionally tight):
 * - `autocapture: false` — no implicit element/click tracking. We will
 *   opt-in to specific events with `posthog.capture("...")` as the
 *   product team decides what to measure. Avoids accidentally shipping
 *   finance amounts / member PII to the analytics pipeline.
 * - `capture_pageview: false` and `capture_pageleave: false` — manual
 *   route tracking will be wired later if/when we want it.
 * - `disable_session_recording: true` — replay stays off until we have
 *   a documented PII masking policy for finance / member views.
 * - `disable_surveys: true` — feature unused for now.
 * - `capture_exceptions: true` — replaces the Sentry global error
 *   handler. Captures unhandled `window` errors and unhandled promise
 *   rejections; React 19 root error callbacks remain available for
 *   manual `posthog.captureException(err)` calls later if we want to
 *   forward errors caught by a UI boundary.
 *
 * `release` is set from the build-time `VITE_APP_VERSION` so events
 * group by deploy (the same value vite.config.ts derives from the
 * host's git SHA).
 */

import posthog from "posthog-js";

// Feature flags must resolve on every DEPLOYED build (staging + production), so PostHog initializes whenever
// this is a production build that has a token. Local `vite dev` is not PROD, so it stays uninitialised and the
// app uses the flag override (localStorage "ibada:flags" / VITE_FLAG_OVERRIDES) plus the fail-safe defaults.
const isDeployed = import.meta.env.PROD && Boolean(import.meta.env.VITE_POSTHOG_PROJECT_TOKEN);

// Event capture + exception capture stay PRODUCTION-only: staging gets flags, never analytics. `captureEvent`
// (shared/lib/analytics.ts) enforces the same VITE_APP_ENV === "production" gate for explicit events.
const isProduction = isDeployed && import.meta.env.VITE_APP_ENV === "production";

if (isDeployed) {
  posthog.init(import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com",
    defaults: "2026-01-30",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    disable_surveys: true,
    capture_exceptions: isProduction,
    loaded: (client) => {
      const release = import.meta.env.VITE_APP_VERSION;
      if (typeof release === "string" && release.length > 0) {
        client.register({ release });
      }
      // Tell flag evaluation which environment this build is, WITHOUT persisting it as a person trait
      // (the same user spans staging + prod). Lets a flag target `app_env` for per-environment control
      // from a single shared PostHog project.
      const appEnv = import.meta.env.VITE_APP_ENV;
      if (typeof appEnv === "string" && appEnv.length > 0) {
        client.setPersonPropertiesForFlags({ app_env: appEnv });
      }
    },
  });
}

/** True when PostHog is actually initialised (deployed build with a token) — guards flag-refresh side effects. */
export const isPostHogActive = isDeployed;

export { posthog };
